import { Button, cn, Input } from '@1trading/react';
import { CornerDownLeft, Globe, KeyRound, type LucideIcon, Search, UserRound } from 'lucide-react';
import { type ChangeEvent, type FocusEvent, type FormEvent, type KeyboardEvent, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StatusChip } from '@/components/ui/status-chip';
import { abuseSubjectPath } from '@/features/abuse/abuse-meta';
import { parseSubjectQuery, type SubjectQueryKind } from '@/features/abuse/subject-query';
import type { AbuseSubjectType } from '@/services/api/abuse';

const QUERY_KIND_LABELS: Record<SubjectQueryKind, string> = { ip: 'IP', uuid: 'UUID' };

const CANDIDATE_LABELS: Record<AbuseSubjectType, string> = { user: 'Open account (PID)', apiKey: 'Open API key', ip: 'Open IP' };

const CANDIDATE_ICONS: Record<AbuseSubjectType, LucideIcon> = { user: UserRound, apiKey: KeyRound, ip: Globe };

/** Opens an abuse subject's page straight from an IP address, PID or API key id */
export const SubjectSearch = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [isPanelOpen, setPanelOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const hintId = useId();
  const query = parseSubjectQuery(input);
  const candidates = query?.candidates ?? [];
  const activeCandidate = candidates[activeIndex];
  const isListShown = isPanelOpen && candidates.length > 0;
  const isHintShown = isPanelOpen && query === null && input.trim() !== '';
  const optionId = (index: number) => `${listId}-option-${index}`;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
    setActiveIndex(0);
    setPanelOpen(true);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeCandidate) return;
    setPanelOpen(false);
    navigate(abuseSubjectPath(activeCandidate));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];

    if (event.key === 'Escape') setPanelOpen(false);
    if (!isListShown || step === undefined) return;
    event.preventDefault();
    setActiveIndex((index) => (index + step + candidates.length) % candidates.length);
  };

  // Only focus leaving the form closes the panel, not focus moving between its controls
  const handleBlur = (event: FocusEvent<HTMLFormElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setPanelOpen(false);
  };

  return (
    <form className="relative w-[28rem] max-w-full" onBlur={handleBlur} onFocus={() => setPanelOpen(true)} onSubmit={handleSubmit}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-activedescendant={isListShown ? optionId(activeIndex) : undefined}
            aria-autocomplete="list"
            aria-controls={listId}
            aria-describedby={isHintShown ? hintId : undefined}
            aria-expanded={isListShown}
            aria-label="Open an abuse subject"
            className="pr-16 pl-9"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Open an IP, PID or API key"
            role="combobox"
            value={input}
          />
          {query ? (
            <StatusChip className="absolute top-1/2 right-2 -translate-y-1/2" size="sm" variant="info">
              {QUERY_KIND_LABELS[query.kind]}
            </StatusChip>
          ) : null}
        </div>
        <Button disabled={!activeCandidate} type="submit">
          Open
        </Button>
      </div>
      {isListShown ? (
        <div
          aria-label="Matching subjects"
          className="absolute top-full right-0 left-0 z-20 mt-1.5 flex flex-col rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          id={listId}
          role="listbox"
        >
          {candidates.map((candidate, index) => {
            const Icon = CANDIDATE_ICONS[candidate.subjectType];
            const isActive = index === activeIndex;

            return (
              <Link
                aria-selected={isActive}
                className={cn('flex items-center gap-2.5 rounded-sm px-2.5 py-2 text-sm outline-none hover:bg-muted', isActive && 'bg-muted')}
                id={optionId(index)}
                key={candidate.subjectType}
                onClick={() => setPanelOpen(false)}
                // Safari does not focus a clicked link, so without this the input's blur closes the panel before the click lands
                onMouseDown={(event) => event.preventDefault()}
                role="option"
                tabIndex={-1}
                to={abuseSubjectPath(candidate)}
              >
                <Icon aria-hidden className="size-4 text-muted-foreground" />
                <span className="flex-1">
                  {CANDIDATE_LABELS[candidate.subjectType]} {candidate.subjectType === 'ip' ? <span className="font-mono">{candidate.subjectId}</span> : null}
                </span>
                {isActive ? (
                  <kbd className="inline-flex items-center gap-1 font-sans text-xs text-muted-foreground">
                    <CornerDownLeft aria-hidden className="size-3" />
                    Enter
                  </kbd>
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : null}
      {isHintShown ? (
        <p className="absolute top-full right-0 left-0 z-20 mt-1.5 rounded-md border bg-popover px-3 py-2 text-xs text-muted-foreground shadow-md" id={hintId}>
          Enter an IPv4 or IPv6 address, a PID or an API key id
        </p>
      ) : null}
    </form>
  );
};
SubjectSearch.displayName = 'SubjectSearch';
